import { reportBelongsToSite } from '../reportSiteIdentity';
/**
 * L1 — site-level composite roll-up from pillar scores + data-confidence penalty.
 */
import { deriveIntentForSelectedAssessment, selectedAssessmentSedIdentity } from '../generationImpact/selectedAssessmentIntent';
import { containsTruthViolation } from '../audit/qaSeverityPolicy';
import { REPORT_TYPE_REGISTRY, reportTypeStage, type ReportTypeId } from '../../config/reportTypes';
import { resolvePromotedGenerationScenario } from '../generationImpact/promotedScenario';
import { selectedGridLedPlanningReadinessReason } from '../generationImpact/gridLedStandbyPlanningReadiness';
import type { SiteEntity } from '../../types/site';
import type { SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { PillarCompositeScore, SiteComposite, DecisionStageStatus, ScreeningOutcome } from '../../types/portfolioComposite';
import { effectivePhases } from '../projectIntent';
import { resolveIntentPowerStack } from '../powerStack';
import {
  DEFAULT_BANDS,
  bandForBenefitComposite,
  toDecisionScore,
  type DecisionBandConfig,
} from './decisionBands';
import {
  computeDataConfidencePenalty,
  toDataConfidenceBreakdown,
} from './dataConfidenceCalculator';
import { assignQuadrant, riskScoreFromComposite } from './quadrant';
import { computeOpportunityScore, opportunityBreakdownFromScore } from './opportunityScorer';
import { PILLAR_IDS, PILLARS, isAdvisoryPillar, isDecisionApplicablePillar, reportsForPillar, type PillarId } from './pillarRegistry';
import {
  PORTFOLIO_CONFIG,
  RED_PILLAR_DECISION_THRESHOLD,
  UNREADABLE_PILLAR_SCORE,
} from './portfolioConfig';
import {
  computeSourceFloor,
  describeSourceFloor,
  floorLowers,
  type SourceFloor,
} from './sourceFloor';
import { requiredReportsForGo, sourceFloorPolicy } from '../pathToGo/mandatoryReports';
import {
  buildReportPlan,
  latestUsableSavedReportOfType,
  planItemCompletesStage,
  type BuildReportPlanInput,
} from '../pathToGo/reportPlan';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';
import { getDisplayVerdict } from '../reportVerdict';
import {
  assessGenerationImpactTestFitApplicability,
  generationImpactTestFitScopeFromSite,
} from '../generationImpact/testFitApplicability';
import {
  composePillarScore,
  findingSeverityPenaltyForPillar,
  scorePillarFromReport,
  type PillarScoreContribution,
} from './pillarScorer';
import {
  analysisDecisionMaterialityToken,
  resolveAnalysisDecisionMateriality,
} from '../pathToGo/analysisApplicability';

/** Per-pillar quality-hold discount records (see PillarCompositeScore.qualityDiscounts). */
type PillarQualityDiscounts = NonNullable<PillarCompositeScore['qualityDiscounts']>;

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/**
 * The "always stale" sentinel `lastComputedAt` that `_staleStampSiteComposite` stamps when a report
 * changes. Date.parse(epoch) is older than any maxAge, so a stamped composite reliably reads as stale.
 */
export const COMPOSITE_STALE_EPOCH_ISO = '1970-01-01T00:00:00.000Z';

/**
 * Bump whenever scorer semantics change without a corresponding site/report
 * mutation. `fp10` recomputes every persisted composite because Power
 * Availability left the always-required floor, which changes the mandatory set
 * the source floor is computed over.
 * `fp9` recomputes every persisted composite so EVERY generated
 * analysis carries floor authority, not just the mandatory set.
 * `fp8` recomputes every persisted composite so risks on single-pillar
 * reports reach the pillar they concern, and a denied withdrawal permit grades
 * blocking rather than merely concerning.
 * `fp7` recomputes every persisted composite so a pillar whose source
 * report exists but could not be read is RETAINED in the aggregate instead of
 * deleted — without the bump the false-GO arithmetic stays frozen in cache.
 * `fp6` recomputes every persisted composite so the SOURCE FLOOR is
 * applied to the stored band — without it a cached composite keeps serving the
 * uncapped band to every portfolio surface, which is the exact defect the floor
 * exists to close.
 * `fp5` recomputes every persisted composite so `decisionRationale`
 * is rebuilt from evidence rather than the band constant — without it a cached
 * composite keeps serving "No material risk barriers" indefinitely, since
 * nothing about the site or its reports changed.
 * `fp4` recomputes every persisted composite onto the corrected
 * applicability rule: `not-applicable` now requires the pillar to also be
 * `missing`, and unmeasured decision weight is charged regardless of
 * applicability. Both are scorer-semantics changes with no site/report
 * mutation, so without this bump a composite stored under `fp3` would compare
 * FRESH and be served verbatim with its old stamp — keeping a measured,
 * possibly red, pillar out of the aggregate. `fp3` adds model/BOM applicability state so a saved authority
 * crossing its minimum-input threshold invalidates a cached decision even when
 * no report identity changed. Persisted `fp1`/`fp2` composites therefore
 * refresh onto the site-aware denominator automatically.
 */
/**
 * `fp11` recomputes every persisted composite onto the corrected declared-load
 * resolution. `intentDeclaresLoad` now resolves through `resolveUltimateCampusMW`
 * rather than reading `powerRequirementMW`, so a PHASED intent finally declares
 * its load — which puts `power-availability` back into the mandatory set, and
 * the mandatory set is what `computeSourceFloor` uses to decide whether a red
 * report floors to NO-GO or merely caps at CONDITIONAL. Composites stored under
 * `fp10` for phased sites carry a band computed with power treated as
 * non-mandatory and would otherwise be served verbatim, two bands too high.
 */
/**
 * `fp12` recomputes onto two further band-semantics changes: an ADVISORY
 * report type no longer contributes a source floor (Connectivity was capping
 * bands the rollup documents it can never gate), and a site whose power pillar
 * is unmeasured can no longer publish the top band.
 */
/**
 * `fp13` recomputes so the persisted `decisionRationale` stops reporting an
 * UNREADABLE pillar (a red report that matched no evaluator regex) as "No
 * source analysis yet" — a caption denying the very reports that produced the
 * band. Cached composites carry that sentence and nothing else invalidates it.
 */
/**
 * `fp14` recomputes because an OMISSION-STAMPED report now caps the band. A
 * report whose evidence is provably absent carries no headline score, so it
 * previously contributed no floor at all — a site LOST a ceiling by having its
 * evidence gap correctly identified. Paired with dropping the fabricated
 * sentiment index on a barren scan, which without this change would have made
 * affected sites read BETTER.
 */
// fp15 (STAGE 5.2) — `PILLARS.power.sourceReports` gained 'feasibility', and
// `reportsForPillar` drives `scorePillarFromReport`. Every persisted composite
// must recompute or a cached verdict would keep serving a power pillar scored
// from the OLD source set — the stale-verdict failure mode a scorer-semantics
// change always carries.
// fp16 (2026-09-05) — the `unreadable` classification is now provenance-aware
// (`dedicatedSourceUnreadable`), so a pillar whose only contributor was a
// FALLBACK source is dropped from the mean instead of scoring 3.0 at full
// weight. That changes computed composites, and persisted ones carry neither
// the flag nor the corrected aggregate, so they must recompute.
// fp17 (2026-09-05, P1.2) — `water-resource` joined ALWAYS_REQUIRED_FOR_GO.
// fp20 (2026-09-06, two-stage review) — the screening SCORE joined the
// composite's stage checklist, the screening result distinguishes hard from
// mitigable reds, and the two-stage hold counts CURRENT analyses (the Decision
// Guide's `reportSatisfiesGo`) whenever the SED is at hand.
// fp19 (2026-09-06, two-stage Phase 3) — the required set grew: environmental
// (Stage 1) and financial (Stage 2) for every site, supply chain for every
// site with an on-site power path. The set feeds `resolveTwoStageHold`,
// `resolveDecisionStages` and `computeSourceFloor`, so every persisted
// composite must recompute.
// fp18 (2026-09-06, two-stage Phase 2) — the composite carries a two-stage
// `decisionHold` (stage-1-incomplete / stage-2-locked / stage-2-incomplete)
// and the promoted scenario joined the inputs fingerprint (`scenario:` part).
// `requiredReportsForGo` feeds `computeSourceFloor` below, so a red water
// report that used to CAP a site at CONDITIONAL (non-mandatory ⇒ mitigable)
// now FLOORS it to NO-GO. Persisted composites carry the old band and must
// recompute. (P1.10 rides on the same bump: an undeclared power strategy over
// grid-coded phases no longer drops Generation & Impact from that set. So do
// P1.5 / P1.7: `RiskCategory` gained `water`, and the environmental report's
// `environmentalRiskMatrix` rows now reach the finding-severity penalty
// through `scoringRisks.ts`, which can lower the environmental and regulatory
// pillars of a site whose persisted composite never saw those rows.)
// fp21 (2026-09-07, two-stage Phase 3 part B): `water-resource` is retired and
// leaves the required set; the water pillar now reads feasibility and
// Generation & Impact — every persisted composite recomputes.
// fp23 — social scores require dated local stance, never generic prose/context.
// fp24 — stage-one completion binds the current semantic analysis authority.
// fp25 — successful saved analyses remain calculation inputs while freshness
// is disclosed separately; queued or failed refreshes no longer erase them.
// fp26 (2026-09-30, owner stage moves) — environmental moved Stage 1 → Stage 2
// and public sentiment left the required set (an additional analysis with no
// stage). The persisted `decisionStages` (per-stage required/present types,
// the Stage 1 outcome and the Stage-1-only screening score, which no longer
// counts the environmental and social pillars) and `decisionHold` (the stage
// an absent environmental report holds; an absent sentiment report no longer
// holds) change, and `requiredReportsForGo` feeds `computeSourceFloor`, so
// every persisted composite recomputes. Pillar weights are unchanged.
// fp27 — saved calculation inputs do not authorize a positive decision band
// when the enabled semantic gate withholds current decision eligibility.
// fp28: unknown zoning remains missing; floors are zoning/land-use scoped.
export const COMPOSITE_INPUTS_FINGERPRINT_VERSION = 'fp28';

export type CompositeAnalysisContext = Pick<BuildReportPlanInput,
  'analysisGateEnabled' | 'analysisSnapshots' | 'currentAnalysisInputsByDomain'>;

/** A network/authority failure cannot opt a gate deployment into legacy mode. */
export const UNAVAILABLE_COMPOSITE_ANALYSIS_CONTEXT: CompositeAnalysisContext = Object.freeze({
  analysisGateEnabled: true, analysisSnapshots: [], currentAnalysisInputsByDomain: {},
});

export function compositeAnalysisFingerprint(context?: CompositeAnalysisContext): string {
  if (context?.analysisGateEnabled !== true) return 'legacy';
  return hashCanonicalJson({
    gate: true,
    analyses: (context.analysisSnapshots ?? []).map((entry) => ({
      analysisId: entry?.revision?.analysisId ?? '',
      siteId: entry?.revision?.siteId ?? '',
      domain: entry?.revision?.domain ?? entry?.snapshot?.domain ?? '',
      inputHash: entry?.revision?.inputHash ?? '',
      contentHash: entry?.revision?.contentHash ?? '',
      snapshotPath: entry?.revision?.snapshotPath ?? '',
      snapshotInputHash: entry?.snapshot?.inputHash ?? '',
      snapshotContentHash: entry?.snapshot?.contentHash ?? '',
      // Evaluation timestamps change on harmless polling, readiness does not.
      readiness: entry?.revision?.readiness
        ? { ...entry.revision.readiness, evaluatedAt: undefined }
        : null,
    })).sort((a, b) => a.domain.localeCompare(b.domain) || a.analysisId.localeCompare(b.analysisId)),
    currentInputs: context.currentAnalysisInputsByDomain ?? {},
  });
}

/**
 * The CURRENT inputs a staleness check compares the stored composite against
 * (scoring audit M-tier, 2026-07-02). `reports` may be the full report list —
 * the fingerprint applies the same `reportsForSite` selection the composite
 * itself uses, so passing a pre-filtered site list is equivalent (idempotent).
 */
export interface CompositeInputsContext {
  site: SiteEntity;
  reports: SiteRagReport[];
  analysisContext?: CompositeAnalysisContext;
}

/**
 * Deterministic fingerprint of a site composite's INPUTS (scoring audit
 * M-tier, 2026-07-02). Covers the subset of invalidation-relevant inputs that
 * is available at BOTH compute time (client store / server admin read) and
 * read time:
 *
 *   - the composite-eligible reports — the exact set `computeSiteComposite`
 *     selects via `reportsForSite` ("verdict-eligible" = the reports that feed
 *     the composite/verdict; there is no narrower eligibility concept in the
 *     scorer) — as sorted `reportId@createdAt` tokens. A held report carries an
 *     `@h` suffix so a hold/promotion flip on the SAME report (id + createdAt
 *     unchanged) still moves the fingerprint, and an `@m` suffix while only
 *     Firestore metadata is present so immutable-body hydration invalidates a
 *     composite that was necessarily computed from incomplete content;
 *   - the site's SED pointer (`sedId` + `sedVersion`) — a SED rebuild bumps
 *     the version and invalidates the data-confidence inputs;
 *   - the intent/config provenance stamp (`siteConfigUpdatedAt`, written by
 *     `updateSiteProjectIntent` et al.) — the closest available
 *     "intentUpdatedAt"; intent moves capacity/load-relative grading.
 *   - the site-aware Financial/BOM activation token — minimum-input readiness
 *     changes the applicable decision-pillar denominator.
 *
 * NOT covered (documented gaps, fall back to the wall-clock/epoch paths):
 * evidence-document content edits that do not bump `sedVersion`, and band-config
 * changes (display-band only). Absent fields serialize as empty segments so
 * older sites still produce a stable fingerprint.
 */
export function computeCompositeInputsFingerprint(args: CompositeInputsContext): string {
  const { site, reports } = args;
  const eligible = reportsForSite(site, reports);
  const tokens = new Set<string>();
  for (const r of eligible) {
    const held = resolveReportQualityHold(r) ? '@h' : '';
    const metadataOnly = r._metadataOnly === true ? '@m' : '';
    const sourceIdentity = hashCanonicalJson({
      sourceHash: r.reportRevision?.sourceHash, documentHash: r.reportRevision?.documentHash,
      receipt: r.deterministicScreeningValidation,
    });
    tokens.add(`${r.id}@${r.createdAt ?? ''}${held}${metadataOnly}@source:${sourceIdentity}`);
  }
  const reportPart = [...tokens].sort().join(',');
  const sedPart = `${site.sedId ?? ''}:${site.sedVersion ?? ''}`;
  const intentPart = site.siteConfigUpdatedAt ?? '';
  const applicabilityPart = analysisDecisionMaterialityToken(site);
  // P1.4c (W6) — the declared power strategy is an INPUT of the composite
  // (it decides `decisionHold`), so declaring it must invalidate a cached
  // composite that still carries the hold.
  const strategyPart = site.typedPreferencesDefaults?.['power-availability']?.btmStrategy ?? 'undeclared';
  // TWO-STAGE (fp18) — the promoted scenario decides the Stage 2 hold, so
  // promoting (or invalidating) one must invalidate a cached hold.
  const planningIdentity = selectedAssessmentSedIdentity(site.generationAssessmentSelection);
  const scenarioPart = planningIdentity ? `planning:${planningIdentity}`
    : `${site.projectIntent?.promotedGenerationScenarioId ?? 'none'}:${site.promotedGenerationScenarioSnapshot?.id ?? ''}`;
  return `${COMPOSITE_INPUTS_FINGERPRINT_VERSION}|reports:${reportPart}|sed:${sedPart}|intent:${intentPart}|applicability:${applicabilityPart}|strategy:${strategyPart}|scenario:${scenarioPart}|analyses:${compositeAnalysisFingerprint(args.analysisContext)}`;
}

/**
 * RC-1 read-side guard. True when a site composite is missing, epoch-invalidated (a report changed its
 * inputs since it was computed), or its inputs changed / it aged out. Read paths (Scoring Engine deep
 * dive, Decision Guide) must recompute on stale-OR-absent — guarding only on absence renders the
 * pre-report cached composite ("no source reports" / wrong scores) after generation stale-stamps it.
 *
 * Fingerprint staleness (scoring audit M-tier, 2026-07-02): when BOTH the stored
 * composite carries an `inputsFingerprint` AND the caller passes the CURRENT
 * inputs (`currentInputs`), staleness is decided by fingerprint equality —
 * mismatch ⇒ stale, match ⇒ fresh (no wall-clock aging; unchanged inputs mean
 * an unchanged composite). The 24h wall-clock remains the fallback whenever a
 * fingerprint can't be compared (pre-fingerprint persisted composites, or call
 * sites without input context). The epoch sentinel is an EXPLICIT invalidation
 * and always wins — even over a matching fingerprint.
 */
export function isSiteCompositeStale(
  composite: { lastComputedAt?: string; inputsFingerprint?: string } | null | undefined,
  maxAgeMs: number = 24 * 60 * 60 * 1000,
  currentInputs?: CompositeInputsContext | null,
): boolean {
  if (!composite) return true;
  const iso = composite.lastComputedAt;
  if (!iso || iso === COMPOSITE_STALE_EPOCH_ISO) return true;
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return true;
  const stored = composite.inputsFingerprint;
  if (currentInputs?.analysisContext?.analysisGateEnabled === true
    && !stored?.startsWith(`${COMPOSITE_INPUTS_FINGERPRINT_VERSION}|`)) return true;
  if (currentInputs && typeof stored === 'string' && stored.length > 0) {
    return computeCompositeInputsFingerprint(currentInputs) !== stored;
  }
  return Date.now() - ts > maxAgeMs;
}

/**
 * Part I S5 — the THREE distinguishable zero-states for a per-pillar render.
 * The bug ("0 everywhere") conflated all three into a confident "0". This
 * accessor is the single source of truth for which one a pillar is in:
 *
 *   - 'recomputing'  — the site composite is STALE (a report changed its inputs
 *                      and the read-side recompute has not landed yet). The
 *                      cached numbers are pre-report — NEVER render them. Show
 *                      "Recomputing scores…".
 *   - 'awaiting'     — the pillar is MISSING (`.missing === true`: no source
 *                      report feeds it). Refuse to emit a numeric score; show
 *                      "Awaiting Data". (A missing pillar carries a display-only
 *                      worst-case 0 stamp internally, which must NOT read as a
 *                      measured 0.)
 *   - 'measured'     — a genuine measured score (including a real measured 0).
 *                      `score` is populated and safe to render.
 *
 * It deliberately does NOT reconstruct the composite from pillars — per-pillar
 * WORST_CASE_MISSING and the aggregate HARD_MISSING differ by design.
 */
export type PillarDisplayState =
  | { kind: 'recomputing' }
  | { kind: 'not-applicable'; reason?: string }
  | { kind: 'awaiting' }
  | { kind: 'measured'; score: number };

export function displayPillarScore(
  pillar: {
    score?: number;
    missing?: boolean;
    applicability?: 'applicable' | 'not-applicable';
    applicabilityReason?: string;
  } | null | undefined,
  opts: { stale: boolean },
): PillarDisplayState {
  if (opts.stale) return { kind: 'recomputing' };
  if (pillar?.applicability === 'not-applicable') {
    return {
      kind: 'not-applicable',
      ...(pillar.applicabilityReason ? { reason: pillar.applicabilityReason } : {}),
    };
  }
  if (!pillar || pillar.missing) return { kind: 'awaiting' };
  return { kind: 'measured', score: typeof pillar.score === 'number' ? pillar.score : 0 };
}

/** Latest report of `type` on the site (newest `createdAt` wins). */
export function latestReportOfType(reports: readonly SiteRagReport[], type: ReportTypeId): SiteRagReport | null {
  const matches = reports.filter((r) => (r.reportType ?? 'feasibility') === type);
  if (matches.length === 0) return null;
  return [...matches].sort((a, b) => Date.parse(b.createdAt ?? '') - Date.parse(a.createdAt ?? ''))[0] ?? null;
}

export function reportsForSite(site: SiteEntity, allReports: SiteRagReport[]): SiteRagReport[] {
  return allReports.filter(report => reportBelongsToSite(site, report));
}

/** Weight multiplier a HELD (draft / needs-revision) report contributes at. */
export const QUALITY_HOLD_DISCOUNT = 0.5;

/**
 * Held/draft detection for the composite's report-selection layer (scoring
 * audit M-tier, 2026-07-02). A report is HELD when `isDraft === true` (Cost P3
 * draft tier — iteration artifact, export-barred) or `qualityHold ===
 * 'needs-revision'` (Report P4 — the reviewer refused to publish it clean).
 * `qualityHold` is stamped onto the report body + metadata doc by the
 * generation job (`QualityHoldStampedReport`) but is not on the SiteRagReport
 * type, so it is read structurally here. Returns the discount REASON, or null
 * for a clean report. `needs-revision` wins when both flags are set (the more
 * specific signal).
 */
export function resolveReportQualityHold(
  report: SiteRagReport | null | undefined,
): 'draft' | 'needs-revision' | null {
  if (!report) return null;
  if ((report as { qualityHold?: unknown }).qualityHold === 'needs-revision') return 'needs-revision';
  // AUDIT-ADVISORY (2026-08-22): every open-audit shape joins the discount —
  // the surfaces promise "included at reduced confidence" for audit-flagged
  // reports, so the math must match for ALL flag shapes, including blocking
  // truth violations that never received a hold stamp.
  if (report.qualityReview?.verdict === 'needs-revision') return 'needs-revision';
  if (containsTruthViolation(report.qaFindings)) return 'needs-revision';
  if (report.isDraft === true) return 'draft';
  return null;
}

/**
 * Latest report of a type — NEWEST WINS, hold or not (2026-08-22, William:
 * audit-advisory). This supersedes the 2026-07-02 hold-aware supersession
 * (last clean revision stayed authoritative over a newer held one): the two
 * verdict halves must read the SAME revision, and the cap stack reads the
 * newest — a split selector meant the composite could score one revision
 * while the caps read another. The other half of the 2026-07-02 decision
 * STANDS: a held/audit-flagged report contributes at `QUALITY_HOLD_DISCOUNT`
 * (0.5) confidence weight, which is what the surfaces' "included at reduced
 * confidence" caveat promises.
 */
function latestReportByType(reports: SiteRagReport[], typeId: ReportTypeId): SiteRagReport | null {
  const matches = reports.filter(r => (r.reportType ?? 'feasibility') === typeId);
  if (matches.length === 0) return null;
  matches.sort((a, b) => Date.parse(b.createdAt ?? '') - Date.parse(a.createdAt ?? ''));
  return matches[0] ?? null;
}

function aggregateBenefitComposite(
  pillars: SiteComposite['pillars'],
  // Part C SP4 (S4) — the AGGREGATE now defaults to HARD_MISSING: missing pillars
  // are excluded from the weighted denominator so the composite reflects only the
  // MEASURED pillars (a missing pillar is no longer indistinguishable from a
  // measured-bad one). The adequacy gate still withholds verdicts on sparse sites.
  missingMode: typeof PORTFOLIO_CONFIG.MISSING_AGGREGATE_MODE = PORTFOLIO_CONFIG.MISSING_AGGREGATE_MODE,
): { composite: number; confidence: number } {
  let sum = 0;
  let w = 0;
  let confSum = 0;
  let confCount = 0;

  for (const pid of PILLAR_IDS) {
    const p = pillars[pid];
    if (!p) continue;
    // CONNECTIVITY-ADVISORY — advisory pillars are informational: scored and
    // rendered, but excluded from the aggregate (both sum and denominator) and
    // from the confidence mean, so they can neither raise nor lower the band.
    if (!isDecisionApplicablePillar(pid, p)) continue;
    // A pillar with NO source report is genuinely absent, and excluding it keeps
    // the composite over measured evidence. A pillar whose source report EXISTS
    // but yielded no readable signal is a different thing entirely: excluding
    // THAT deletes a report from the arithmetic, and because it leaves both the
    // numerator and the denominator, removing a below-mean pillar RAISES the
    // composite. The site scores better for holding evidence nobody can read.
    // FIX 2026-09-05 — was `p.missing && sourceReportIds.length > 0`, which
    // could not tell "the report that answers this pillar produced nothing"
    // from "an unrelated report merely exists". Every pillar with a FALLBACK
    // source (feasibility feeds power/land/regulatory/financial) has a
    // non-empty source list the moment that fallback report is generated, so
    // the old test scored those pillars at UNREADABLE_PILLAR_SCORE = 3 at FULL
    // weight on sites that had no such evidence at all — RAISING the composite
    // of any site whose real evidence sits below 3.0, and inverting the very
    // invariant the comment above states. `composePillarScore` now stamps the
    // provenance directly.
    const unreadable = p.missing && p.dedicatedSourceUnreadable === true;
    if (missingMode === 'HARD_MISSING' && p.missing && !unreadable) continue;
    sum += (unreadable ? UNREADABLE_PILLAR_SCORE : p.score) * p.weight;
    w += p.weight;
    if (!p.missing) {
      confSum += p.confidence;
      confCount += 1;
    }
  }

  const benefitComposite = w > 0 ? sum / w : PORTFOLIO_CONFIG.WORST_CASE_SCORE;
  const confidence = confCount > 0 ? confSum / confCount : 0;
  return { composite: benefitComposite, confidence };
}

/**
 * Compose the decision rationale from the evidence that actually produced the
 * band — never from the band constant.
 *
 * `band.meaning` is a fixed string attached to a numeric range. The GO row read
 * "Proceed to full diligence. No material risk barriers." and was emitted
 * verbatim for ANY composite in that range, consulting no pillar, constraint,
 * risk or per-source verdict. It therefore printed "no material risk barriers"
 * beside sites carrying red source reports whose pillars had been dropped from
 * the aggregate. A sentence that cannot see a constraint must not make claims
 * about constraints.
 *
 * This states only what was measured, and it names gaps rather than implying
 * their absence is a clean result.
 */
function composeDecisionRationale(
  pillars: Record<PillarId, PillarCompositeScore>,
  confidenceTag: string,
): string {
  const red: string[] = [];
  const unmeasured: string[] = [];
  const unreadable: string[] = [];
  for (const pillarId of PILLAR_IDS) {
    if (pillarId === 'evidenceQuality') continue;
    if (isAdvisoryPillar(pillarId)) continue;
    const pillar = pillars[pillarId];
    if (!pillar) continue;
    if (pillar.missing) {
      // A pillar can be `missing` for two OPPOSITE reasons, and saying the
      // wrong one is the same class of false claim as the "No material risk
      // barriers" string this function exists to replace.
      //
      //   - no source report at all      → nothing has been analysed;
      //   - a source report EXISTS but   → the analysis was run and could not
      //     produced no gradable signal     be scored ("unreadable", the state
      //                                     `aggregateBenefitComposite` retains
      //                                     at UNREADABLE_PILLAR_SCORE).
      //
      // Branching on `missing` alone reported the second as the first: on the
      // Rivergate fixture — three RED analyses whose prose matches none of the
      // pillar regexes — the caption read "No source analysis yet for Water
      // Resource, Environmental, Public Sentiment" on a site carrying red
      // reports for precisely those three, directly beside the source-floor
      // sentence naming one of them.
      if ((pillar.sourceReportIds?.length ?? 0) > 0) {
        unreadable.push(PILLARS[pillarId].label);
      } else {
        unmeasured.push(PILLARS[pillarId].label);
      }
      continue;
    }
    if (toDecisionScore(pillar.score) > RED_PILLAR_DECISION_THRESHOLD) {
      red.push(PILLARS[pillarId].label);
    }
  }

  const parts: string[] = [];
  if (red.length > 0) {
    parts.push(
      `${red.length === 1 ? 'Red pillar' : 'Red pillars'}: ${red.join(', ')}.`,
    );
  }
  if (unreadable.length > 0) {
    parts.push(
      `Analysed but not scoreable — the source report could not be graded: ${unreadable.join(', ')}.`,
    );
  }
  if (unmeasured.length > 0) {
    parts.push(`No source analysis yet for ${unmeasured.join(', ')}.`);
  }
  if (parts.length === 0) {
    // The strongest claim the engine is entitled to make: it speaks only for
    // the pillars it actually measured, and says so.
    parts.push('No red pillar across the measured decision evidence.');
  }
  if (confidenceTag === 'C') {
    parts.push('Data confidence is coarse; treat the band as provisional.');
  }
  return parts.join(' ');
}


/**
 * TWO-STAGE DEVELOPER DECISION PIPELINE — the composite-level hold (Phase 2,
 * owner decisions 2 and 4, 2026-09-06). A band is published only when every
 * required Stage 1 analysis exists, a generation scenario is promoted (a site
 * that DECLARED grid-only can never promote one and is not locked), and every
 * required Stage 2 analysis exists. This is the same `decisionHold` channel
 * P1.4c added for the undeclared strategy, so the portfolio band, quadrant,
 * map pins, CSV and the Decision Guide withhold together. The caller supplies
 * current decision-eligible types when the semantic gate is enabled; legacy
 * mode retains saved calculation presence. Saved stage completion is separate.
 * Never lifts a band (monotonic-down): it can only replace one with a hold, and
 * the caller skips it entirely when the composite band is already no-go — a
 * decline stands.
 */
export function resolveTwoStageHold(input: {
  site: Partial<SiteEntity>;
  mandatoryTypes: readonly ReportTypeId[];
  presentTypes: ReadonlySet<ReportTypeId>;
  gridOnly: boolean;
  completedScreeningTypes?: ReadonlySet<ReportTypeId>;
  /** Saved work remains complete even when its current decision authority fails. */
  savedTypes?: ReadonlySet<ReportTypeId>;
}): SiteComposite['decisionHold'] {
  const missing = (stage: 1 | 2): ReportTypeId[] =>
    input.mandatoryTypes.filter((type) => reportTypeStage(type) === stage && !input.presentTypes.has(type));
  const label = (types: readonly ReportTypeId[]): string =>
    types.map((type) => REPORT_TYPE_REGISTRY[type]?.shortLabel ?? type).join(', ');
  const stage1Missing = missing(1);
  if (stage1Missing.length > 0) {
    return {
      code: 'stage-1-incomplete',
      label: 'Decision pending',
      reason: stage1Missing.some(type => input.savedTypes?.has(type))
        ? `Early screening needs current, quality-approved ${label(stage1Missing)} before a band can be published. Review the saved analysis and its evidence or quality holds.`
        : input.completedScreeningTypes && stage1Missing.every(type => input.completedScreeningTypes?.has(type))
        ? `Early screening complete — ${label(stage1Missing)} still has unmeasured evidence before a band can be published.`
        : `Early screening incomplete — generate ${label(stage1Missing.filter(type => !input.completedScreeningTypes?.has(type)))} before a band can be published.`,
    };
  }
  if (!input.gridOnly || input.site.generationAssessmentSelection?.kind === 'grid-led-standby-planning') {
    const promoted = resolvePromotedGenerationScenario(
      input.site.projectIntent,
      input.site.promotedGenerationScenarioSnapshot,
      null,
      input.site.generationAssessmentSelection,
      input.site.generationProjectInputBinding,
    );
    if (!promoted && !(input.site.generationAssessmentSelection?.kind === 'grid-led-standby-planning' && selectedGridLedPlanningReadinessReason(input.site) === null)) {
      return {
        code: 'stage-2-locked',
        label: 'Decision pending',
        reason: 'Mature validation needs a promoted generation scenario — solve and promote one before a band can be published.',
      };
    }
  }
  const stage2Missing = missing(2);
  if (stage2Missing.length > 0) {
    return {
      code: 'stage-2-incomplete',
      label: 'Decision pending',
      reason: stage2Missing.some(type => input.savedTypes?.has(type))
        ? `Mature validation needs current, quality-approved ${label(stage2Missing)} before a band can be published. Review the saved analyses and their evidence or quality holds.`
        : `Mature validation incomplete — generate ${label(stage2Missing)} before a band can be published.`,
    };
  }
  return undefined;
}

/**
 * The SCREENING SCORE (0–100) and its coverage, over the decision pillars fed
 * by Stage 1 sources. Same pillar scores the composite aggregates — SED typed
 * data, report findings, economic research — restricted to what early
 * screening can know. Null when no Stage-1-fed pillar is measured yet.
 */
export function screeningScoreFromPillars(
  pillars: SiteComposite['pillars'],
): { score: number; scoreCoverage: number } | null {
  const fed = PILLAR_IDS.filter((id) => {
    const def = PILLARS[id];
    return !def.advisory && (def.sourceReports ?? []).some((type) => reportTypeStage(type) === 1);
  });
  let weightAll = 0;
  let weightMeasured = 0;
  let weighted = 0;
  for (const id of fed) {
    const def = PILLARS[id];
    const pillar = pillars[id];
    weightAll += def.weight;
    if (!pillar || pillar.missing) continue;
    const score = typeof pillar.score === 'number' && Number.isFinite(pillar.score) ? pillar.score : 0;
    weightMeasured += def.weight;
    weighted += def.weight * Math.max(0, Math.min(5, score));
  }
  if (weightMeasured <= 0) return null;
  return {
    score: Math.round((weighted / weightMeasured) * 20),
    scoreCoverage: weightAll > 0 ? weightMeasured / weightAll : 0,
  };
}

/**
 * The per-stage checklist behind `resolveTwoStageHold`, persisted on the
 * composite so the dashboard and its rail show completed analysis milestones.
 * Stage-one completion reads the Early Screening Assessment alone since the
 * owner's 2026-09-30 moves (Environmental is Stage 2; Public Sentiment is an
 * additional analysis outside both stages); the independent positive-evidence
 * hold still withholds a decision band.
 * Computed for every declared-or-undeclared site; never a substitute for GO.
 */
export function resolveDecisionStages(input: {
  site: Partial<SiteEntity>;
  mandatoryTypes: readonly ReportTypeId[];
  presentTypes: ReadonlySet<ReportTypeId>;
  gridOnly: boolean;
  strategyDeclared: boolean;
  /** The site's reports — the Stage 1 outcome reads their display verdicts. */
  reports?: readonly SiteRagReport[];
}): NonNullable<SiteComposite['decisionStages']> {
  const promoted = input.gridOnly
    ? null
    : resolvePromotedGenerationScenario(
        input.site.projectIntent,
        input.site.promotedGenerationScenarioSnapshot,
        null,
        input.site.generationAssessmentSelection,
      input.site.generationProjectInputBinding,
      );
  const latestOfType = (type: ReportTypeId): SiteRagReport | null => latestReportOfType(input.reports ?? [], type);
  const stageOf = (stage: 1 | 2): DecisionStageStatus => {
    const requiredTypes = input.mandatoryTypes.filter((type) => reportTypeStage(type) === stage);
    const presentTypes = requiredTypes.filter((type) => input.presentTypes.has(type));
    const missingTypes = requiredTypes.filter((type) => !input.presentTypes.has(type));
    const base: DecisionStageStatus = {
      stage,
      requiredTypes,
      presentTypes,
      missingTypes,
      locked: stage === 2 && (input.site.generationAssessmentSelection?.kind === 'grid-led-standby-planning'
        ? selectedGridLedPlanningReadinessReason(input.site) !== null : !input.gridOnly && promoted === null),
    };
    // The screening result: Stage 1 only, and only once every required
    // screening report is present — read from the SAME display verdicts the
    // source floor reads, so the outcome and the floor cannot disagree.
    if (stage !== 1 || requiredTypes.length === 0 || missingTypes.length > 0 || !input.reports) return base;
    const tiers = requiredTypes.map((type) => {
      const report = latestOfType(type);
      return { type, tier: report ? getDisplayVerdict(report, deriveIntentForSelectedAssessment(input.site.projectIntent, input.site.generationAssessmentSelection)).tier : 'none' };
    });
    // A red DECLINES only under a 'hard' floor policy — the same policy the
    // source floor applies. A MITIGABLE red caps the call at CONDITIONAL, so it
    // is a concern carried forward, never a decline: the screening result must
    // never contradict the band. (Since 2026-09-30 Stage 1 requires only the
    // Early Screening Assessment — environmental is Stage 2 and public
    // sentiment is an additional analysis — so a mitigable red no longer
    // reaches this result; the rule stays for any mitigable Stage 1 source.)
    const hardRed = tiers.filter((t) => t.tier === 'red' && sourceFloorPolicy(t.type) === 'hard').map((t) => t.type);
    const softRed = tiers.filter((t) => t.tier === 'red' && sourceFloorPolicy(t.type) !== 'hard').map((t) => t.type);
    const amber = tiers.filter((t) => t.tier === 'amber').map((t) => t.type);
    if (hardRed.length === 0 && amber.length + softRed.length === 0 && tiers.some(t => t.tier === 'none')) return base;
    const outcome: ScreeningOutcome = hardRed.length > 0 ? 'declined' : amber.length + softRed.length > 0 ? 'concerns' : 'clear';
    return { ...base, outcome, concernTypes: [...hardRed, ...softRed, ...amber] };
  };
  return {
    screening: stageOf(1),
    validation: stageOf(2),
    gridOnly: input.gridOnly,
    strategyDeclared: input.strategyDeclared,
  };
}

export function computeSiteComposite(args: {
  site: SiteEntity;
  customerId: string;
  reports: SiteRagReport[];
  evidence?: SiteEvidenceDocument | null;
  analysisContext?: CompositeAnalysisContext;
  /**
   * Override for the AGGREGATE missing-pillar mode (how missing pillars affect
   * the COMPOSITE SCORE). Defaults to PORTFOLIO_CONFIG.MISSING_AGGREGATE_MODE
   * (HARD_MISSING). The per-pillar RENDER stamp is always MISSING_RENDER_MODE
   * and is unaffected by this override. Used by tests + pathToGo.
   */
  missingPillarMode?: typeof PORTFOLIO_CONFIG.MISSING_AGGREGATE_MODE;
  bandConfig?: DecisionBandConfig | null;
}): SiteComposite {
  const { site: canonicalSite, customerId, reports, evidence, missingPillarMode = PORTFOLIO_CONFIG.MISSING_AGGREGATE_MODE, bandConfig } = args;
  const activeIntent = deriveIntentForSelectedAssessment(canonicalSite.projectIntent, canonicalSite.generationAssessmentSelection);
  const site = activeIntent === canonicalSite.projectIntent ? canonicalSite : { ...canonicalSite, projectIntent: activeIntent };
  const siteReports = reportsForSite(site, reports);
  const generationApplicability = assessGenerationImpactTestFitApplicability(
    generationImpactTestFitScopeFromSite(site),
  );
  const mandatoryTypes = requiredReportsForGo(
    site.projectIntent,
    site.typedPreferencesDefaults?.['power-availability'],
    site,
  );
  const consideredTypes = [
    ...new Set(
      siteReports.map(report => (report.reportType ?? 'feasibility') as ReportTypeId),
    ),
  ];
  const calculationTypes = [...new Set<ReportTypeId>([
    ...mandatoryTypes,
    ...consideredTypes,
  ])];
  // Keep raw history for lifecycle + fingerprinting, but every calculation
  // below reads one safe successful carrier per type. A newer failed/partial
  // retry can no longer displace the last usable saved revision.
  const calculationReports = calculationTypes.flatMap(type => {
    const report = latestUsableSavedReportOfType(siteReports, type);
    return report ? [report] : [];
  });
  const phases = effectivePhases(site.projectIntent);
  const capacityMW = phases.totalCapacityMW ?? 0;

  // Site FACILITY load (MW) for load-relative pillar grading (power pillar grades
  // grid headroom against THIS site's demand, not absolute thresholds). Prefer the
  // basis+PUE-aware facility load; fall back to the raw phased capacity.
  const siteLoadMW = resolveIntentPowerStack(site.projectIntent).facilityMW
    ?? (capacityMW > 0 ? capacityMW : null);

  const pillars = {} as SiteComposite['pillars'];
  const sourceReportIds = new Set<string>();
  const dynamicPillarMateriality = {
    interconnection: resolveAnalysisDecisionMateriality('interconnection', site),
    financial: resolveAnalysisDecisionMateriality('financial', site),
    supplyChain: resolveAnalysisDecisionMateriality('supply-chain-risk', site),
  } as const;
  const decisionMaterialReports = calculationReports.filter(report => (
    resolveAnalysisDecisionMateriality(
      report.reportType ?? 'feasibility',
      site,
    ).decisionMaterial
  ));

  /**
   * Post-scoring contribution adjustments, applied IN ORDER after
   * `scorePillarFromReport` (never inside it — pillar internals are owned
   * elsewhere and their signatures are frozen):
   *   1. stale-report penalty (pre-existing behaviour, unchanged): a >1yr-old
   *      source report loses 0.20 confidence;
   *   2. quality-hold discount (M-tier, 2026-07-02): a HELD report (draft /
   *      needs-revision) contributes at QUALITY_HOLD_DISCOUNT (0.5) weight.
   *      `composePillarScore` weights contributions by confidence, so the
   *      multiplier halves both the contribution's weight in the pillar blend
   *      AND its share of the pillar confidence — a held report informs the
   *      score but never carries it at full strength. The marker rides three
   *      surfaces: the rationale suffix, a structured `quality-hold-discount`
   *      constraint (folded into `appliedConstraints` chips by
   *      composePillarScore), and the per-pillar `qualityDiscounts` record.
   *   3. finding-severity penalty (R4a): UNMITIGATED structured `RiskItem`s
   *      whose category maps to THIS pillar lower the contribution's SCORE,
   *      bounded at MAX_FINDING_SEVERITY_PENALTY (one decision band). This is
   *      deliberately a SCORE adjustment, not a confidence one: adjustments 1
   *      and 2 are both statements about how far to TRUST the report, whereas a
   *      real site risk is a statement about the SITE. Routing risk through
   *      confidence — as `confidenceFromReport` does for QA blocks — is exactly
   *      why findings used to be unable to move the verdict.
   * A report with no unmitigated risks for this pillar takes paths 1–2 only —
   * numerically identical to before.
   */
  const adjustContribution = (
    contrib: PillarScoreContribution,
    report: SiteRagReport,
    pillarDiscounts: PillarQualityDiscounts,
    pillarId: PillarId,
  ): void => {
    const ageMs = Date.now() - Date.parse(report.createdAt ?? '');
    if (ageMs > 365 * 24 * 60 * 60 * 1000) {
      contrib.confidence = Math.max(0, contrib.confidence - 0.20);
      contrib.rationale += ` (stale report penalty)`;
    }
    const holdReason = resolveReportQualityHold(report);
    if (holdReason) {
      contrib.confidence *= QUALITY_HOLD_DISCOUNT;
      contrib.rationale += ` (quality hold — weighted 50%)`;
      contrib.constraints = [
        ...(contrib.constraints ?? []),
        {
          kind: 'quality-hold-discount',
          label: 'Quality hold — weighted 50%',
          penalty: QUALITY_HOLD_DISCOUNT,
        },
      ];
      pillarDiscounts.push({ reportId: report.id, discount: QUALITY_HOLD_DISCOUNT, reason: holdReason });
    }
    // 3. R4a — bounded finding-severity penalty on the SCORE. Clamped to the
    //    configured floor so a heavily-flagged pillar bottoms out rather than
    //    going negative, and recorded as a structured constraint so the deep
    //    dive can attribute the movement to named risks.
    const finding = findingSeverityPenaltyForPillar(report, pillarId);
    if (finding.penalty > 0) {
      contrib.score = Math.max(PORTFOLIO_CONFIG.SCORE_MIN, contrib.score - finding.penalty);
      const label = `${finding.count} unmitigated ${finding.worstSeverity ?? 'risk'}-severity finding${finding.count === 1 ? '' : 's'}`;
      contrib.rationale += ` (${label} — score reduced ${finding.penalty.toFixed(2)})`;
      contrib.constraints = [
        ...(contrib.constraints ?? []),
        {
          kind: 'finding-severity-penalty',
          label,
          penalty: finding.penalty,
        },
      ];
    }
  };

  for (const pillarId of PILLAR_IDS) {
    const def = PILLARS[pillarId];
    const contributions: PillarScoreContribution[] = [];
    const pillarDiscounts: PillarQualityDiscounts = [];

    if (def.structuredSource === 'auditFindings') {
      // Newest revision wins for the evidence-quality pillar too. Selecting
      // the newest report *that happened to contain qaFindings* let a warning
      // on a superseded revision survive forever when its clean replacement
      // omitted an empty QA block. That contradicted latestReportByType and
      // amplified historical QA debt into the current decision basis.
      const byNewest = [...decisionMaterialReports].sort(
        (a, b) => Date.parse(b.createdAt ?? '') - Date.parse(a.createdAt ?? ''),
      );
      const auditReport = byNewest[0];
      if (auditReport) {
        try {
          const contrib = scorePillarFromReport(pillarId, auditReport.reportType ?? 'feasibility', auditReport, evidence, siteLoadMW, canonicalSite);
          adjustContribution(contrib, auditReport, pillarDiscounts, pillarId);
          contributions.push(contrib);
        } catch {
          // Malformed or incomplete report must not crash composite calculation or invent a valid score.
        }
      }
    } else {
      const types = reportsForPillar(pillarId);
      for (const typeId of types) {
        const report = latestReportByType(calculationReports, typeId);
        // Phase 3 part B (2026-09-07): feasibility is the water pillar's DEDICATED
        // source only through its water-screen chapter (`waterAnalysis`). A
        // feasibility report generated before the chapter existed carries no
        // water evidence at all — it must neither measure the pillar nor count
        // as an unreadable dedicated source (which would score 3.0 at full
        // weight). Such a report is simply not a water source; the pillar then
        // reads a legacy water report. Generation & Impact only contributes a
        // frozen duty shortfall; adequate equipment duty cannot certify rights.
        if (report && pillarId === 'water' && typeId === 'feasibility' && !report.waterAnalysis) continue;
        if (report && pillarId === 'water' && typeId === 'power-availability'
          && report.generationImpactProjection?.waterResource?.adequacy !== 'fail') continue;
        if (report) {
          try {
            const contrib = scorePillarFromReport(pillarId, typeId, report, evidence, siteLoadMW, canonicalSite);
            adjustContribution(contrib, report, pillarDiscounts, pillarId);
            contributions.push(contrib);
          } catch {
            // Malformed or incomplete report must not crash composite calculation or invent a valid score.
          }
        }
      }
    }

    const composed = composePillarScore(site.id, pillarId, contributions);
    const materiality =
      pillarId === 'interconnection'
        ? dynamicPillarMateriality.interconnection
        : pillarId === 'financial'
          ? dynamicPillarMateriality.financial
          : pillarId === 'supplyChain'
            ? dynamicPillarMateriality.supplyChain
            : null;
    // A domain with no saved model AND no measurement is genuinely "available
    // later" work. A domain that is already MEASURED is not: dropping it would
    // remove a real, possibly red, pillar from the aggregate. Because
    // `aggregateBenefitComposite` skips non-applicable pillars in BOTH the
    // numerator and the denominator, deleting a below-mean pillar RAISES the
    // composite and moves the site toward GO — an unsafe failure direction on
    // the product's headline output. `PILLARS.financial` draws on
    // ['financial','feasibility'] and `pathwayLcoeAtGen` is stamped on every
    // generated report, so this pillar IS measured for most existing sites.
    // Gate on `composed.missing` so a pillar backed by a real scored report
    // always keeps its seat. See pillarRegistry: the feasibility fallback
    // "never turns a previously-measured pillar missing".
    composed.applicability = materiality?.decisionMaterial === false && composed.missing
      ? 'not-applicable'
      : 'applicable';
    if (materiality) {
      composed.applicabilityReason = materiality.reason;
      composed.applicabilityReasonCode = materiality.reasonCode;
    }
    // Attach the per-report discount markers OUTSIDE composePillarScore (its
    // signature is frozen); absent when no contributing report was held.
    if (pillarDiscounts.length > 0) composed.qualityDiscounts = pillarDiscounts;
    // Part C SP4 (S4) — the per-pillar RENDER stamp is governed by
    // MISSING_RENDER_MODE (WORST_CASE_MISSING), INDEPENDENT of the aggregate
    // mode: a missing pillar still carries score 0 + the "No source reports"
    // floor so the deep dive renders it as "Awaiting Data". This stamp is
    // display-only — the aggregate (HARD_MISSING) excludes missing pillars from
    // the composite regardless of what is stamped here.
    if (composed.missing && PORTFOLIO_CONFIG.MISSING_RENDER_MODE === 'WORST_CASE_MISSING') {
      composed.score = PORTFOLIO_CONFIG.WORST_CASE_SCORE;
      composed.confidence = 0;
      composed.missing = true;
      // Part C SP2 — record the no-source floor as a structured constraint so
      // the deep dive can attribute the worst-case score (the deep dive itself
      // renders missing pillars as "Awaiting Data", but downstream consumers can
      // read the structured reason).
      composed.appliedConstraints = [{
        kind: 'no-source-floor',
        label: 'No source reports available',
      }];
    }
    pillars[pillarId] = composed;
    // Preserve the informational per-pillar score, but do not let an inactive
    // domain inflate the decision-source count or enter confidence penalties.
    if (isDecisionApplicablePillar(pillarId, composed)) {
      for (const rid of composed.sourceReportIds) sourceReportIds.add(rid);
    }
  }

  const { composite: rawBenefit, confidence: pillarConfidence } = aggregateBenefitComposite(pillars, missingPillarMode);

  // Unmeasured decision-material pillar weight. `aggregateBenefitComposite`
  // runs HARD_MISSING and drops missing pillars from the DENOMINATOR, so a
  // never-generated report used to cost nothing at all — the composite was
  // simply computed over whatever existed. Summing the missing weight here and
  // feeding it to the confidence ladder makes absence cost CONFIDENCE instead:
  // the site trends toward the 'C' tag and `evaluateSiteDataAdequacy` withholds
  // ("we don't know enough") rather than asserting a band it cannot support.
  // `evidenceQuality` is excluded — it is derived FROM the penalty below, so
  // counting it here would be circular.
  const unmeasuredPillarWeight = PILLAR_IDS.reduce((sum, pid) => {
    if (pid === 'evidenceQuality') return sum;
    // CONNECTIVITY-ADVISORY — a STATIC advisory pillar's absence costs nothing:
    // it is not decision-material for any site, so unmeasured advisory weight
    // must not push a site toward the 'C' tag / withheld verdict.
    //
    // Site-specific inactive pillars are NOT excluded here, deliberately.
    // Skipping them charged the 0.23 held by Financial + Supply Chain only
    // against owners who had done the diligence: a site with a saved financial
    // model but no analysis yet took the penalty and a lower trust tag, while
    // an otherwise identical site that saved nothing took none and scored
    // strictly better. That is the "absence is free" incentive this mechanism
    // exists to close (see portfolioConfig), reintroduced through the
    // applicability gate. An unmeasured decision pillar is a real gap in the
    // decision basis whether or not the user has started the work — the same
    // fixed-frame reasoning as `decisionPillarCoverage`.
    if (isAdvisoryPillar(pid)) return sum;
    return pillars[pid]?.missing ? sum + PILLARS[pid].weight : sum;
  }, 0);

  // SED D1.1 — thread the already-in-scope SED (destructured above) into the
  // data-confidence penalty so an UNRESOLVED HIGH-severity evidence conflict
  // worsens data confidence. NO signature/import change — `evidence` is
  // undefined for every non-scorer caller ⇒ all-zero ⇒ additive.
  // Confidence belongs to the same decision basis as the pillar score. Using
  // every historical revision made QA findings cumulative across superseded
  // reports: a mature site could never recover from old warnings and was
  // eventually forced to confidence C even when every current source was
  // healthy. `sourceReportIds` is assembled above from the exact newest
  // revision selected for each pillar, so this filter preserves all current
  // QA/conflict penalties without allowing retired revisions to vote again.
  const decisionSourceReports = calculationReports.filter(report => sourceReportIds.has(report.id));
  const penaltyResult = computeDataConfidencePenalty(
    site.id, decisionSourceReports, evidence, unmeasuredPillarWeight,
  );

  // Azraq protocol: penalty worsens the site. On benefit scale we subtract;
  // equivalent to adding penalty on the risk/decision scale.
  const finalComposite = clamp(
    rawBenefit - penaltyResult.penalty,
    PORTFOLIO_CONFIG.SCORE_MIN,
    PORTFOLIO_CONFIG.SCORE_MAX,
  );

  const scoredBand = bandForBenefitComposite(finalComposite, bandConfig);
  // SOURCE FLOOR — a red or amber mandatory source report caps the band before
  // it is persisted. Previously this cap existed ONLY inside
  // `buildExecutiveDecisionRollup`, which runs on site-level surfaces; every
  // portfolio surface reads this persisted composite, so a site whose Decision
  // Guide read NO-GO still showed a green GO pill on the snapshot, map, risk
  // matrix and decision queue. Capping here means the stored band is already
  // correct and every surface inherits it.
  //
  // The score is deliberately NOT altered — this mirrors the rollup's
  // monotonic-down semantics, where a cap lowers the call without rewriting the
  // benefit arithmetic that produced it.
  //
  // EVERY generated analysis is considered, not just the mandatory set. A red
  // environmental, water or sentiment report on a site whose intent never
  // triggered its gate previously carried NO floor authority at all — it could
  // not move the call by a single band, which is how a site holding three red
  // reports published GO. The mandatory set still governs which reds may
  // DECLINE a site; everything else caps at CONDITIONAL, so real evidence is
  // heard without any one non-gating report being able to reject the project.
  const sourceFloor: SourceFloor | null = computeSourceFloor(
    calculationReports,
    consideredTypes,
    { mandatory: mandatoryTypes, intent: site.projectIntent },
  );
  const floored = floorLowers(sourceFloor, scoredBand.id)
    ? (bandConfig?.bands ?? DEFAULT_BANDS).find(b => b.id === sourceFloor.ceiling) ?? scoredBand
    : scoredBand;

  /**
   * A data centre cannot read PROCEED with its power never analysed.
   *
   * Power Availability left `ALWAYS_REQUIRED_FOR_GO` when it became
   * authority-gated on a declared load, which was the right call — before a
   * load exists the report has no denominator. But the consequence was not:
   * a site with no declared load never makes power mandatory, so
   * `mandatorySatisfied` stays true, PATH 4 never fires, and the power pillar
   * — unmeasured, with no source report — is skipped by the aggregate
   * ENTIRELY, leaving both numerator and denominator. Losing the heaviest
   * pillar RAISES the mean: eight green pillars and no power analysis compute
   * to roughly 4.3 and publish "Proceed" for a site whose power question was
   * never asked.
   *
   * The weight-coverage floor does not catch this — 0.69 of a 0.90 frame is
   * 0.767, comfortably above `MIN_DECISION_WEIGHT_COVERAGE`. It only bites
   * when SEVERAL pillars are missing; one heavy pillar slips through.
   *
   * This CAPS rather than declines, matching the stated guardrail that a
   * severe finding may cap at CONDITIONAL but never force NO-GO. The site can
   * still be conditional on everything else; it simply cannot be called an
   * unconditional proceed on an unanswered question.
   */
  const powerPillar = pillars.power;
  const powerUnmeasured = powerPillar == null || powerPillar.missing === true;
  const powerCap = powerUnmeasured && floored.id === 'go'
    ? (bandConfig?.bands ?? DEFAULT_BANDS).find(b => b.id === 'conditional')
    : undefined;
  const band = powerCap ?? floored;

  /**
   * P1.4c (W6) — mirror the Decision Guide's PATH 1b on the composite.
   *
   * The rollup withholds the call while the accountable grid / hybrid /
   * BTM-only strategy is undeclared, but every PORTFOLIO surface reads this
   * persisted composite, which carried a coloured band regardless — so the
   * cluster chip, quadrant, map and CSV published a colour the Decision stage
   * refused to. The band is still computed (below) so nothing downstream loses
   * its arithmetic; the hold is a separate, honest field that
   * `deriveSiteVerdict` turns into a withheld "Decision pending" band.
   * Undeclared is the DEFAULT state of a site, and the same predicate PATH 1b
   * reads is read here so the two engines cannot disagree.
   */
  const strategyDeclared = generationApplicability.strategyDeclared;
  // Calculation usability and freshness are separate. A successfully saved,
  // hydrated revision continues to contribute while a refresh is stale,
  // queued or failed; the plan still exposes that lifecycle to the UI.
  const calculationPlan = (() => {
    try {
      return buildReportPlan({
        ...args.analysisContext,
        site, sed: evidence ?? null, reports: siteReports, intent: site.projectIntent,
        powerAvailabilityPreferences: site.typedPreferencesDefaults?.['power-availability'],
        requiresGenerationImpact: generationApplicability.required,
        requiredTypes: mandatoryTypes, scopeTypes: calculationTypes, preflightByType: new Map(),
      });
    } catch {
      return [];
    }
  })();
  const satisfiedTypes = new Set<ReportTypeId>(
    calculationPlan
      .filter(item => item.contributesToCalculations === true)
      .map(item => item.type),
  );
  const completedTypes = new Set<ReportTypeId>();
  for (const item of calculationPlan) {
    if (planItemCompletesStage(item)) completedTypes.add(item.type);
  }
  // Saved results remain calculation inputs and completed workflow tasks.
  // Publication is a separate authority: an enabled semantic gate must use
  // the plan's current, quality-approved decision signal. A stale or held
  // saved result cannot silently authorize GO/CONDITIONAL through arithmetic.
  const publicationTypes = args.analysisContext?.analysisGateEnabled === true
    ? new Set(calculationPlan.filter(item => item.satisfiesGo).map(item => item.type))
    : satisfiedTypes;
  // The two-stage hold withholds only a band ABOVE no-go (see resolveTwoStageHold):
  // a hard decline is published, never hidden behind "Decision pending".
  const decisionHold: SiteComposite['decisionHold'] = strategyDeclared
    ? band.id === 'noGo' ? undefined : resolveTwoStageHold({
        site,
        mandatoryTypes,
        presentTypes: publicationTypes,
        completedScreeningTypes: completedTypes,
        ...(args.analysisContext?.analysisGateEnabled === true ? { savedTypes: satisfiedTypes } : {}),
        gridOnly: generationApplicability.status === 'grid-only',
      })
    : {
        code: 'power-strategy-undeclared',
        label: 'Decision pending',
        reason:
          'Power strategy undeclared — confirm grid-only, hybrid, or BTM-only in Project Intent '
          + 'before a GO or CONDITIONAL call can be published.',
      };
  const decisionStages = resolveDecisionStages({
    site,
    mandatoryTypes,
    presentTypes: completedTypes,
    gridOnly: generationApplicability.status === 'grid-only',
    strategyDeclared,
    reports: calculationReports,
  });
  const decisionScore = toDecisionScore(finalComposite);
  const riskScore = riskScoreFromComposite(finalComposite);
  const oppResult = computeOpportunityScore(site, calculationReports, evidence);
  const opp = {
    opportunityScore: oppResult.score,
    opportunityConfidence: oppResult.confidence,
    opportunityRationale: oppResult.rationale,
    opportunityBreakdown: opportunityBreakdownFromScore(oppResult),
  };
  const quadrant = assignQuadrant(riskScore, opp.opportunityScore);
  const confidenceTag = penaltyResult.confidenceTags[0] ?? 'V';

  // Wire evidenceQuality pillar from data-confidence output
  const dcBenefit = clamp(
    PORTFOLIO_CONFIG.SCORE_MAX - penaltyResult.penalty * (PORTFOLIO_CONFIG.SCORE_MAX / PORTFOLIO_CONFIG.MAX_DATA_COMPLETENESS_PENALTY),
    0,
    5,
  );
  pillars.evidenceQuality = {
    ...pillars.evidenceQuality,
    score: dcBenefit,
    confidence: penaltyResult.penalty < 0.05 ? 0.9 : penaltyResult.penalty < 0.15 ? 0.7 : 0.5,
    rationale: `Data confidence penalty ${penaltyResult.penalty.toFixed(2)} (${confidenceTag})`,
    missing: false,
    // Part C SP2 — surface the data-confidence penalty (previously only in the
    // rationale string) as a structured deep-dive chip. Additive only; the
    // score above is unchanged.
    appliedConstraints: [{
      kind: 'data-confidence-penalty',
      label: `Data confidence ${confidenceTag}`,
      penalty: penaltyResult.penalty,
    }],
  };

  return {
    siteId: site.id,
    siteName: site.name,
    customerId,
    capacityMW,
    composite: finalComposite,
    confidence: pillarConfidence,
    lastComputedAt: new Date().toISOString(),
    // Fingerprint of the inputs THIS composite was computed from (M-tier,
    // 2026-07-02). `siteReports` is the already-selected eligible set;
    // computeCompositeInputsFingerprint re-applies reportsForSite, which is
    // idempotent on a pre-filtered list, so compute-time and read-time
    // fingerprints agree for the same world state.
    inputsFingerprint: computeCompositeInputsFingerprint({ site, reports: siteReports, analysisContext: args.analysisContext }),
    sourceReportIds: [...sourceReportIds],
    dataCompletenessPenalty: penaltyResult.penalty,
    dataConfidenceBreakdown: toDataConfidenceBreakdown(penaltyResult),
    confidenceTag,
    dataConfidencePillar: {
      label: 'Data Confidence',
      score: dcBenefit,
      penalty: penaltyResult.penalty,
      tag: confidenceTag,
      rationale: `Aggregate QA penalty ${penaltyResult.penalty.toFixed(2)} across ${decisionSourceReports.length} selected decision-source report(s)`,
    },
    decisionBand: band.id,
    decisionBandLabel: band.label,
    ...(decisionHold ? { decisionHold } : {}),
    decisionStages: (() => {
      const screening = screeningScoreFromPillars(pillars);
      return screening
        ? { ...decisionStages, screening: { ...decisionStages.screening, ...screening } }
        : decisionStages;
    })(),
    decisionRationale: [
      ...(floorLowers(sourceFloor, scoredBand.id) ? [describeSourceFloor(sourceFloor)] : []),
      // A cap with no stated reason is the defect this rationale was rewritten
      // to remove; say why the call is not an unconditional proceed.
      ...(powerCap
        ? ['Power availability has not been analysed for this site, so the call cannot exceed CONDITIONAL.']
        : []),
      composeDecisionRationale(pillars, confidenceTag),
    ].join(' '),
    decisionScore,
    riskScore,
    opportunityScore: opp.opportunityScore,
    opportunityConfidence: opp.opportunityConfidence,
    opportunityRationale: opp.opportunityRationale,
    opportunityBreakdown: opp.opportunityBreakdown,
    quadrant,
    pillars,
  };
}

export function applyPenaltyToComposite(benefitComposite: number, penalty: number): number {
  return clamp(
    benefitComposite - penalty,
    PORTFOLIO_CONFIG.SCORE_MIN,
    PORTFOLIO_CONFIG.SCORE_MAX,
  );
}
